import { useState, useEffect } from 'react';
import { Modal, IconButton, InputAdornment, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useToast } from 'use-toast-mui';
import { formatterCurrency } from '../../../utils/methods';
import CommonModalButton from '../../../form-components/CommonModalButton';

import {
  AdjustModalContainer,
  AdjustModalHeader,
  AdjustModalTitle,
  AdjustModalBody,
  AdjustTabsContainer,
  AdjustTab,
  AdjustInfoCard,
  AdjustInfoRow,
  AdjustInfoLabel,
  AdjustInfoTitle,
  AdjustInfoBadge,
  AdjustAmountCard,
  AdjustAmountInput,
  AdjustResultBar,
  AdjustResultAmount,
  AdjustMemoCard,
  AdjustMemoInput,
  AdjustModalFooter,
} from './SalarySettlementStyled';

const AdjustAmountModal = ({ open, onClose, itemName, itemType, currentAmount, onSave }) => {
  const toast = useToast();
  const [adjustTab, setAdjustTab] = useState('add'); // 'add' | 'subtract'
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');

  useEffect(() => {
    if (open) {
      setAdjustTab('add');
      setAmount('');
      setMemo('');
    }
  }, [open]);

  const numericAmount = parseInt(amount?.replace(/,/g, '') || '0', 10);
  const adjustedAmount =
    adjustTab === 'add' ? currentAmount + numericAmount : currentAmount - numericAmount;

  const handleAmountChange = e => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setAmount(raw ? Number(raw).toLocaleString() : '');
  };

  const handleSave = () => {
    if (numericAmount <= 0) {
      toast.error('조정 금액을 입력해주세요');
      return;
    }
    if (adjustedAmount < 0) {
      toast.error('조정 후 금액이 음수가 될 수 없습니다');
      return;
    }
    onSave?.({
      type: adjustTab,
      amount: numericAmount,
      memo,
      adjustedAmount,
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} disableScrollLock>
      <AdjustModalContainer>
        <AdjustModalHeader>
          <AdjustModalTitle>금액조정</AdjustModalTitle>
          <IconButton onClick={onClose} sx={{ color: '#fff', p: '4px' }}>
            <Close />
          </IconButton>
        </AdjustModalHeader>

        <AdjustModalBody>
          {/* 추가/차감 탭 */}
          <AdjustTabsContainer>
            <AdjustTab active={adjustTab === 'add'} onClick={() => setAdjustTab('add')}>
              추가
            </AdjustTab>
            <AdjustTab active={adjustTab === 'subtract'} onClick={() => setAdjustTab('subtract')}>
              차감
            </AdjustTab>
          </AdjustTabsContainer>

          {/* 금액조정 대상 */}
          <AdjustInfoCard>
            <AdjustInfoRow>
              <AdjustInfoLabel>금액조정 대상</AdjustInfoLabel>
              <AdjustInfoBadge type={itemType}>
                {itemType === 'pay' ? '지급 항목' : '공제 항목'}
              </AdjustInfoBadge>
            </AdjustInfoRow>
            <AdjustInfoTitle sx={{ mt: '4px' }}>{itemName}</AdjustInfoTitle>
            <AdjustInfoRow sx={{ mt: '12px', pt: '12px', borderTop: '1px solid #f0f0f0' }}>
              <AdjustInfoLabel>현재 금액</AdjustInfoLabel>
              <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#262626' }}>
                {formatterCurrency(currentAmount)}원
              </Typography>
            </AdjustInfoRow>
          </AdjustInfoCard>

          {/* 추가할 조정금액 */}
          <AdjustAmountCard>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#262626' }}>
              {adjustTab === 'add' ? '추가할' : '차감할'} 조정금액
            </Typography>
            <AdjustAmountInput
              fullWidth
              value={amount}
              onChange={handleAmountChange}
              placeholder="0"
              InputProps={{
                endAdornment: <InputAdornment position="end">원</InputAdornment>,
              }}
            />
            <AdjustResultBar>
              조정 후 금액 :&nbsp;
              <AdjustResultAmount color={adjustedAmount >= 0 ? '#4AB3BC' : '#CF1322'}>
                {formatterCurrency(adjustedAmount)}원
              </AdjustResultAmount>
            </AdjustResultBar>
          </AdjustAmountCard>

          {/* 메모 */}
          <AdjustMemoCard>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#262626' }}>
              메모
            </Typography>
            <AdjustMemoInput
              fullWidth
              multiline
              rows={3}
              value={memo}
              onChange={e => setMemo(e.target.value)}
              placeholder="메모를 입력해 주세요."
            />
          </AdjustMemoCard>
        </AdjustModalBody>

        <AdjustModalFooter>
          <CommonModalButton variant="cancel" onClick={onClose}>
            취소
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleSave}>
            저장
          </CommonModalButton>
        </AdjustModalFooter>
      </AdjustModalContainer>
    </Modal>
  );
};

export default AdjustAmountModal;
